// Instant broadcast (Sprint 10, docs/03 §Broadcast). A broadcast is an org-wide
// takeover: one message (optionally an existing media slide) is pushed to EVERY
// screen at once as a hidden, top-priority schedule (kind='broadcast') that a
// normal schedule can never outrank. Types live here (not @seencast/shared yet),
// same pattern as api/reports.ts and api/analytics.ts — the wire shapes below
// mirror the C1 contract exactly.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from './client.ts';
import { qk } from './keys.ts';

/** GET /v1/broadcast/current → null when nothing is live, else the active takeover. */
export interface CurrentBroadcast {
  schedule_id: string;
  message: string;
  media_id: string | null;
  started_at: number;
  /** null = "Until cancelled". */
  expires_at: number | null;
}

/** POST /v1/broadcast body. duration_minutes omitted = until cancelled. */
export interface StartBroadcast {
  message: string;
  media_id?: string;
  duration_minutes?: number;
}

/** POST /v1/broadcast response. */
export interface StartBroadcastResult {
  schedule_id: string;
  media_id: string | null;
  /** How many connected players received the immediate refresh push. */
  delivered: number;
  /** Total screens the takeover targets (delivered ≤ screens_total; the rest poll). */
  screens_total: number;
}

export function getCurrentBroadcast(): Promise<CurrentBroadcast | null> {
  return request<CurrentBroadcast | null>('/broadcast/current');
}

/**
 * Poll the live broadcast every 30s so the org-wide banner appears/clears within
 * a heartbeat even for teammates who didn't start it, and refetch on window focus
 * so returning to the tab is instant.
 */
export function useCurrentBroadcast() {
  return useQuery({
    queryKey: qk.broadcastCurrent,
    queryFn: getCurrentBroadcast,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useStartBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StartBroadcast) =>
      request<StartBroadcastResult>('/broadcast', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.broadcastCurrent });
      // A broadcast creates a hidden schedule and takes over screens now.
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: qk.attention });
    },
  });
}

export function useCancelBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) =>
      request<void>(`/broadcast/${scheduleId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.broadcastCurrent });
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: qk.attention });
    },
  });
}
